import React, { useMemo } from 'react';

import { calculateInvestmentSharesPricePerUnit } from 'app-components/forms/investment/investmentTransactionFormDisplay';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import QAmountField from 'components/QAmountField';

import { investmentTransactionEditFormHooks } from 'components/Forms/InvestmentTransactionEditForm/investmentTransactionEditFormHooks';
import { SecurityAndTagsFields } from './SecurityAndTagsFields';
import { handleNumericKeyDown, numberFromInput, useNumericTextField } from './utils';

export function SharesFields({ expectedSign }: { expectedSign: 1 | -1 }): React.JSX.Element {
  const amount = investmentTransactionEditFormHooks.useField('amount');
  const units = investmentTransactionEditFormHooks.useField('units');
  const unitsInput = useNumericTextField({
    value: units.value,
    onValueChange: units.onValueChange,
    sign: expectedSign,
  });
  const commission = investmentTransactionEditFormHooks.useField('commission');
  const pricePerUnit = useMemo(
    () =>
      calculateInvestmentSharesPricePerUnit({
        amount: amount.value,
        commission: commission.value,
        expectedSign,
        units: units.value,
      }),
    [amount.value, commission.value, expectedSign, units.value],
  );

  return (
    <>
      <SecurityAndTagsFields />
      <Box display='flex' alignItems='center' gap={1}>
        <QAmountField
          id='total-amount'
          editable
          label='Total Amount'
          sx={{ minWidth: 110 }}
          noNegatives
          value={Math.abs(amount.value ?? 0)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            amount.onValueChange(expectedSign * Math.abs(numberFromInput(event.target.value)))
          }
          error={amount.error}
          helperText={amount.helperText}
        />
        =
        <TextField
          id='units'
          label='Shares quantity'
          type='number'
          inputProps={{ min: 1, inputMode: 'decimal' }}
          sx={{ minWidth: 110 }}
          value={unitsInput.value}
          onKeyDown={handleNumericKeyDown}
          onChange={unitsInput.onChange}
          error={units.error}
          helperText={units.helperText}
        />
        x
        <QAmountField
          id='price-per-share'
          sx={{ minWidth: 110, marginBottom: 1 }}
          expectedSign='positive'
          value={pricePerUnit}
          label='Price per share'
        />
        {expectedSign > 0 ? '-' : '+'}
        <QAmountField
          id='commission'
          editable
          sx={{ minWidth: 110 }}
          value={commission.value ?? undefined}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            commission.onValueChange(numberFromInput(event.target.value))
          }
          label='Commission'
          error={commission.error}
          helperText={commission.helperText}
        />
      </Box>
    </>
  );
}
